import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme, useMediaQuery } from '@mui/material';
import EdgeSheet from './EdgeSheet';

/**
 * LocalSubnav - secondary page navigation for edit/detail workspaces.
 *
 * Desktop/tablet: inline vertical sidebar beside the primary content.
 * Mobile: compact trigger button that opens the same navigation inside an
 * EdgeSheet.
 *
 * Props:
 *   title       {string} optional title shown above the nav items
 *   items       {array}  [{ key, label, icon, onClick, dividerBefore, disabled }]
 *   mobileLabel {string} trigger button label on mobile (default 'Tools')
 *   width       {number|string} sidebar width on md+ (default 240)
 */
export default function LocalSubnav({
  title = 'Tools',
  items = [],
  mobileLabel = 'Tools',
  width = 240,
  open: openProp,
  onOpen,
  onClose,
  showMobileTrigger = true,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;

  const handleOpen = () => {
    if (openProp === undefined) {
      setInternalOpen(true);
    }
    onOpen?.();
  };

  const handleClose = () => {
    if (openProp === undefined) {
      setInternalOpen(false);
    }
    onClose?.();
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {!isMobile && title && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
      )}

      <Divider />

      <List disablePadding>
        {items.map((item, index) => (
          <React.Fragment key={item.key ?? item.label ?? index}>
            {item.dividerBefore && <Divider />}
            <ListItemButton
              disabled={item.disabled}
              onClick={() => {
                if (isMobile) {
                  handleClose();
                }
                item.onClick?.();
              }}
            >
              {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
              <ListItemText primary={item.label} />
            </ListItemButton>
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <>
        {showMobileTrigger && (
          <Button
            variant="outlined"
            startIcon={<MenuIcon />}
            onClick={handleOpen}
            sx={{ alignSelf: 'flex-start', mb: 2 }}
          >
            {mobileLabel}
          </Button>
        )}
        <EdgeSheet
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          title={title}
          anchor="right"
          width={320}
        >
          {content}
        </EdgeSheet>
      </>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        width,
        flexShrink: 0,
        alignSelf: 'flex-start',
        overflow: 'hidden',
        borderColor: theme.palette.divider,
      }}
    >
      {content}
    </Paper>
  );
}
